/**
 * Warm the browser HTTP image cache for product pictures while the splash is
 * up. The 3D shop interiors are built lazily on approach (a 40-store mall
 * can't hold every interior's textures in GPU memory at once), so their
 * product images used to be fetched cold the moment the player walked up,
 * which reads as pictures fading in a beat late.
 *
 * This decodes each image through an <img> element that exactly mirrors the
 * render path (surfaces/DynamicTextureSurface's `new Image()` +
 * crossOrigin = "anonymous"), so the bytes land in the same HTTP cache entry
 * the texture load later reads. No Babylon texture is created here, so there
 * is no GPU cost: only the network round trip is paid ahead of time.
 */
export function preloadImageUrls(urls: string[], concurrency: number = 6): Promise<void> {
  const queue: string[] = [...new Set(urls.filter((u: string) => u !== ""))];
  const workers: number = Math.min(concurrency, queue.length);
  if (workers === 0) {
    return Promise.resolve();
  }
  const warmOne = (url: string): Promise<void> =>
    new Promise<void>((resolve) => {
      const img: HTMLImageElement = new Image();
      img.crossOrigin = "anonymous";
      // A failed image is harmless: the on-approach load simply retries it.
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = url;
    });
  const worker = async (): Promise<void> => {
    for (;;) {
      const url: string | undefined = queue.shift();
      if (url === undefined) {
        return;
      }
      await warmOne(url);
    }
  };
  return Promise.all(Array.from({ length: workers }, () => worker())).then(() => undefined);
}
